import * as React from 'react';
import { FontSize, FontWeightType, TextColor } from './title.types';
import { colorMap, fontSizeMapToStandard, fontWeightMap } from './title.constants';

import { cn } from '@restworld/utils-common';
export interface TitleWrapperProps {
  fontSize?: FontSize;
  isCenter?: boolean;
  wrapperStyle?: string;
  desktopOnly?: boolean;
  mobileOnly?: boolean;
  textColor?: TextColor;
}

export const TitleWrapper = ({
  fontSize,
  isCenter,
  wrapperStyle,
  desktopOnly,
  mobileOnly,
  textColor,
  className,
  ...props
}: any) => (
  <span
    className={cn(
      'flex flex-row items-center gap-1.5',
      fontSize && standardTitleVariants[fontSizeMapToStandard(fontSize)],
      isCenter && 'justify-center text-center',
      textColor ? colorMap[textColor] : '',
      desktopOnly ? 'hidden lg:flex' : '',
      mobileOnly ? 'lg:hidden flex' : '',
      className
    )}
    {...props}
  />
);
export const IconWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('inline-flex shrink-0 items-center justify-center', className)} {...props} />
);

export interface TitleContentProps {
  textColor?: TextColor;
  fontWeight?: FontWeightType;
  isPointer?: boolean;
  twStyle?: string;
  variant?: keyof typeof variantMap;
  standardVariant?: keyof typeof standardTitleVariants;
  align?: keyof typeof alignment;
}

export const TitleContent = ({
  textColor,
  fontWeight,
  isPointer,
  twStyle,
  variant,
  standardVariant,
  align,
  className,
  ...props
}: any) => (
  <span
    className={cn(
      textColor ? colorMap[textColor] : '',
      isPointer ? 'cursor-pointer' : '',
      variant ? standardTitleVariants[mapTitleProps(variant)] : '',
      standardVariant ? standardTitleVariants[standardVariant] : '',
      align ? alignment[align] : '',
      fontWeight ? fontWeightMap[fontWeight] : '',
      twStyle && !Array.isArray(twStyle) ? twStyle : '',
      twStyle,
      className
    )}
    {...props}
  />
);

export const standardTitleVariants = {
  // Text 6xl
  'display-1': 'lg:text-[60px] lg:leading-[90px] text-[48px] leading-[72px] font-bold',

  // Text 5xl
  'display-2': 'lg:text-[48px] lg:leading-[72px] text-[48px] leading-[72px] font-semibold',

  // Text 4xl
  h1: 'lg:text-[36px] lg:leading-[54px]  text-[30px] leading-[45px]  font-semibold',

  // Text 3xl
  h2: 'lg:text-[30px] lg:leading-[45px]  text-[24px] leading-[36px] font-semibold',

  // Text 2xl
  h3: 'lg:text-[24px] lg:leading-[36px] text-[24px] leading-[36px] font-semibold',

  // Text xl
  h4: 'lg:text-[20px] lg:leading-[30px] text-[20px] leading-[30px] font-semibold',

  // Text lg
  h5: 'lg:text-[16px] lg:leading-[24px] text-[16px] leading-[24px] font-semibold',
};

export type StandardTitleVariantsProps = keyof typeof standardTitleVariants;

const variantMap = {
  h1: 'lg:text-4xl text-base font-semibold lg:leading-13.5 leading-6  text-black',
  'h1-medium': 'lg:text-4xl text-2xl lg:leading-13.5  leading-9  text-black',
  'h1-small': 'lg:text-4xl text-xl lg:leading-13.5  leading-7.5',
  h2: 'text-3xl font-bold',
  'h2-medium': 'lg:text-3xl lg:leading-[45px] text-2xl leading-7',
  'h2-large': 'lg:text-3xl lg:leading-13.5 text-2xl leading-9',
  h3: 'text-2xl font-bold',
  'h3-large': 'lg:text-2xl lg:leading-9 text-2xl leading-9',
  'h3-medium': 'lg:text-2xl lg:leading-9 text-xl leading-7.5',
  'h3-small': 'lg:text-2xl lg:leading-8 text-lg leading-6.75',
  'h3-xsmall': 'lg:text-2xl lg:leading-7.5 text-lg leading-6.75',
  h4: 'lg:text-xl text-sm  lg:leading-7.5 leading-5.25',
  'h4-xlarge': 'lg:text-xl lg:leading-7.5 text-lg leading-6.75',
  'h4-medium': 'lg:text-xl  text-xl',
  'h4-large': 'lg:text-xl lg:leading-7.5 text-xl leading-7.5',
  'h4-small': 'lg:text-xl lg:leading-7.5 text-base',
  'h4-xsmall': 'lg:text-xl lg:leading-9 text-base leading-6',
  h5: 'text-lg font-bold',
  'h5-large': 'lg:text-lg lg:leading-6.75 text-lg leading-6.75',
  'h5-medium': 'lg:text-lg lg:leading-6.75 text-base leading-6',
  h6: 'text-base font-bold',
  'h6-large': 'lg:text-base text-base',
  'h6-medium': 'lg:text-base lg:leading-6 text-sm leading-5.25',
  'h6-xsmall': 'lg:text-sm lg:leading-5.25 text-sm leading-5.25',
};

const alignment = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

export const mapTitleProps = (variant: TitleContentProps['variant']): StandardTitleVariantsProps => {
  switch (variant) {
    case 'h1':
    case 'h1-medium':
    case 'h1-small':
      return 'h1';
    case 'h2':
    case 'h2-medium':
    case 'h2-large':
      return 'h2';
    case 'h3':
    case 'h3-large':
    case 'h3-medium':
    case 'h3-small':
    case 'h3-xsmall':
      return 'h3';
    case 'h4-medium':
    case 'h4-large':
    case 'h4-xlarge':
    case 'h4-small':
    case 'h4':
    case 'h4-xsmall':
      return 'h4';
    case 'h5':
    case 'h5-large':
    case 'h5-medium':
      return 'h4';
    default:
      return 'h5';
  }
};

/**
 * Maps a standard variant to its corresponding semantic HTML element.
 * Returns undefined for non-semantic variants (display-1, display-2),
 * letting the caller fall back to the default element.
 */
export const standardVariantToHtmlElement = (
  variant?: StandardTitleVariantsProps
): keyof React.JSX.IntrinsicElements | undefined => {
  switch (variant) {
    case 'h1':
      return 'h1';
    case 'h2':
      return 'h2';
    case 'h3':
      return 'h3';
    case 'h4':
      return 'h4';
    case 'h5':
      return 'h5';
    default:
      return undefined;
  }
};

/**
 * Maps a legacy variant to its corresponding semantic HTML element,
 * deriving the heading level from the variant prefix (h1-*, h2-*, …).
 */
export const variantToHtmlElement = (
  variant?: keyof typeof variantMap
): keyof React.JSX.IntrinsicElements | undefined => {
  if (!variant) return undefined;
  const match = variant.match(/^(h[1-6])/);
  return match ? (match[1] as keyof React.JSX.IntrinsicElements) : undefined;
};
